import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type CandidateState, type IssuedToken } from "../../api/client";

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="secondary"
      onClick={() =>
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
      }
    >
      {copied ? "Copied" : label}
    </button>
  );
}

/** The main path: paste the address, choose OAuth, click Allow. */
function OAuthSteps({ mcpUrl }: { mcpUrl: string }) {
  return (
    <ol className="connect-steps">
      <li>
        <h2>Copy this address</h2>
        <div className="copy-row">
          <code>{mcpUrl}</code>
          <CopyButton text={mcpUrl} label="Copy address" />
        </div>
      </li>
      <li>
        <h2>Add it to your AI app</h2>
        <p>
          In ChatGPT, Claude or another AI app, add a new connector in its settings. It may be
          called "custom connector", "MCP server" or "integration". Paste the address. If the app
          asks how to sign in, choose <strong>OAuth</strong>. Leave everything else blank: the app
          fills it in.
        </p>
      </li>
      <li>
        <h2>Approve</h2>
        <p>
          Your app opens a Tekdi page. Click <strong>Allow</strong> and you're taken back to your
          app.
        </p>
      </li>
      <li>
        <h2>Start</h2>
        <p>
          Attach your CV in the chat and say: <em>"Start my Tekdi application."</em>
        </p>
      </li>
    </ol>
  );
}

function IssuedHeader({ issued }: { issued: IssuedToken }) {
  const value = `Bearer ${issued.token}`;
  return (
    <div className="setup-note">
      <p>Add a header in your app:</p>
      <dl className="facts">
        <dt>Name</dt>
        <dd>
          <code>Authorization</code>
        </dd>
        <dt>Value</dt>
        <dd>
          <code>Bearer …</code> (copy it below)
        </dd>
      </dl>
      <label htmlFor="token">Header value (shown once)</label>
      <input id="token" readOnly value={value} onFocus={(e) => e.target.select()} />
      <CopyButton text={value} label="Copy value" />
      <p className="muted">Valid until {new Date(issued.expires_at).toLocaleString()}.</p>
    </div>
  );
}

/** For apps without OAuth support: a bearer token, generated only when asked for. */
function TokenFallback({ state }: { state: CandidateState }) {
  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const issue = useMutation({
    mutationFn: api.issueToken,
    onSuccess: (token) => {
      setIssued(token);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <details className="token-fallback">
      <summary>My app asks for a token or an "Authorization" header</summary>
      <p>
        Some apps (for example Cursor, VS Code and Claude Code) need a token instead of OAuth.
        {state.tested_clients_url && (
          <>
            {" "}
            <a href={state.tested_clients_url}>Apps we've tested</a>.
          </>
        )}
      </p>
      {issued ? (
        <IssuedHeader issued={issued} />
      ) : (
        <>
          <p className="muted">
            You'll see the token only once. Generating a new one disconnects your previous agent.
            {state.token.issued &&
              state.token.expires_at &&
              ` Your current token is valid until ${new Date(state.token.expires_at).toLocaleString()}.`}
          </p>
          <button type="button" disabled={issue.isPending} onClick={() => issue.mutate()}>
            {state.token.issued ? "Generate a new token" : "Generate token"}
          </button>
        </>
      )}
      {issue.isError && <p role="alert">{issue.error.message}</p>}
    </details>
  );
}

export function TokenStep({ state }: { state: CandidateState }) {
  return (
    <section>
      <h1>Connect your AI agent</h1>
      <OAuthSteps mcpUrl={state.mcp_url} />
      <p className="muted">This page updates by itself once your agent is connected.</p>
      <TokenFallback state={state} />
    </section>
  );
}
